import React from 'react';
import PropTypes from 'prop-types';
import * as strings from '@hydra/utils/strings';

import { makeStyles } from '@material-ui/core/styles';

import Translate from '@hydra/common/components/i18n';
import Dot from '@hydra/common/components/Dot';
import { getContrastedColor, getPriSecColors } from '@hydra/utils/colors';
import formatNumber from '@hydra/utils/formatNumber';

import CriteriumTable from '@hydra/common/criterium/CriteriumTable';
import Criterium from '@hydra/common/criterium/Criterium';

import facebookShares from '@hydra/assets/img/share-facebook.svg';
import facebookComments from '@hydra/assets/img/comment-facebook.svg';
import facebookReactions from '@hydra/assets/img/like-facebook.svg';
import hasCritData from '@hydra/utils/hasCritData';
import { useReviewContext } from '@hydra/common/context/review';
import { usePdfTemplateContext } from '@hydra/common/context/pdfTemplate';

const useStyles = makeStyles(theme => ({
  logo: {
    display: 'inline-block',
    marginRight: theme.spacing(1),
    width: '20px',
    height: '20px'
  },
  table: {
    marginBottom: theme.spacing(1)
  },
  url: {
    marginRight: theme.spacing(2)
  },
  dot: {
    marginRight: theme.spacing(0.5)
  },
  urlContainer: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  iconText: {
    [theme.breakpoints.print()]: {
      display: 'inherit !important'
    },
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  }
}));

const shareIcons = {
  facebookShares,
  facebookComments,
  facebookReactions
};

const text = (score) => {
  if (score <= 0.3) {
    return 'shared_count_low';
  } else if (score <= 0.6) {
    return 'shared_count_medium';
  } else {
    return 'shared_count_high';
  }
};

const getShareIcon = (socialNetwork, classes) => {
  return <img src={shareIcons[socialNetwork]} alt={`${socialNetwork} logo`} className={classes.logo} />;
};

const getNetworksNames = data => Object.keys(data).filter(name => name !== 'pinterest');

const useGetRows = ({ socialNetworks, data, competitors }) => {
  const classes = useStyles();
  return socialNetworks.map(socialNetwork => [
    (
      <span key='sharedCount'>
        {getShareIcon(socialNetwork, classes)}
        <Translate content={strings.snakeCase(socialNetwork)} className={classes.iconText} />
      </span>
    ),
    data[socialNetwork] === null ? '-' : formatNumber(data[socialNetwork]),
    ...competitors.map(({ criteria }) => {
      if (!hasCritData(criteria.shared_count)) {
        return '-';
      }
      return formatNumber(criteria.shared_count.data[socialNetwork]);
    })
  ]);
};

const useGetHead = ({ color, competitors, secondary }) => {
  if (competitors.length === 0) {
    return [null, null];
  }
  return [null, <Dot color={color} key={0} />, ...competitors.map((_, i) => <Dot key={i + 1} color={secondary[i]} />)];
};

export const SharedCount = ({ criterium, competitors = [] }) => {
  const classes = useStyles();
  const { isPdf } = useReviewContext();
  const { templateColors } = usePdfTemplateContext();
  const { data, score, url } = criterium;

  const { primary, secondary } = isPdf
    ? templateColors
    : getPriSecColors();
  const color = getContrastedColor(primary);

  const rows = useGetRows({
    socialNetworks: getNetworksNames(data),
    data,
    competitors
  });

  const tableClassNames = {
    head: ['', 'alignRight', ...competitors.map((_, i) => i === 0 ? 'borderLeft' : 'alignRight')],
    body: ['', 'alignRight', ...competitors.map((_, i) => i === 0 ? 'borderLeft' : 'alignRight')]
  };

  return (
    <Criterium
      criterium={criterium}
      variables={{ domain: url }}
    >
      <Translate component='p' content={text(score)} />
      <CriteriumTable
        classNames={tableClassNames}
        className={classes.table}
        head={useGetHead({ color, competitors, secondary })}
        rows={rows}
        fixedColumns={[20 + ((3 - competitors.length) * 20), 20, ...competitors.map(() => 20)]}
        thinHead
        striped
        nowrap
      />
      {competitors.length > 0 && (
        <div className={classes.urlContainer}>
          <span className={classes.url}><Dot color={color} className={classes.dot} />{url}</span>
          {competitors.map(({ url }, i) => <span key={i} className={classes.url}><Dot color={secondary[i]} className={classes.dot} />{url}</span>)}
        </div>
      )}
    </Criterium>
  );
};

SharedCount.propTypes = {
  sharedCount: PropTypes.shape({
    data: PropTypes.object,
    score: PropTypes.number
  }),
  competitors: PropTypes.arrayOf(
    PropTypes.shape({
      criteria: PropTypes.shape({
        shared_count: PropTypes.shape({
          url: PropTypes.string,
          data: PropTypes.shape({
            facebookShares: PropTypes.number,
            facebookComments: PropTypes.number,
            facebookReactions: PropTypes.number
          })
        })
      })
    })
  )
};

export default React.memo(SharedCount);
